import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const MAX_ATTEMPTS = 6;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [data, setData] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        setData(res.data);
        if (res.data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (res.data.status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        // ignore, retry
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-28 text-center md:px-8" data-testid="checkout-result">
      {state === "checking" && (
        <>
          <Loader2 size={48} className="animate-spin text-kkaccent" />
          <h1 className="font-display mt-6 text-2xl font-light">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-zinc-500">Please don't close this window.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={56} className="text-emerald-600" />
          <h1 className="font-display mt-6 text-3xl font-light tracking-tight">Thank you for your order!</h1>
          <p className="mt-3 text-sm text-zinc-500">
            Your payment was successful{data?.amount_total ? ` — ${money(data.amount_total / 100)}` : ""}. A confirmation
            will be sent shortly.
          </p>
          <Link to="/shop" className="mt-8 flex h-12 items-center gap-2 bg-zinc-900 px-8 text-xs uppercase tracking-widest text-white hover:bg-zinc-800">
            Continue Shopping
          </Link>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle size={56} className="text-kksale" />
          <h1 className="font-display mt-6 text-2xl font-light">
            {state === "timeout" ? "Still processing" : "Payment not completed"}
          </h1>
          <p className="mt-3 text-sm text-zinc-500">
            {state === "timeout"
              ? "We're still confirming your payment. Check your email for confirmation."
              : "Your payment could not be confirmed. Please try again."}
          </p>
          <Link to="/cart" className="mt-8 flex h-12 items-center gap-2 border border-zinc-900 px-8 text-xs uppercase tracking-widest hover:bg-zinc-50">
            Back to Cart
          </Link>
        </>
      )}
    </div>
  );
}
